import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Entity, EntityKey} from '../../entity/entity';
import {EntityService} from '../../services/entity-service';
import {StringUtils} from '../../utils/string-utils';
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from '@angular/material/table';
import {EntityLineHeader} from '../../entity/entity-line-header';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {SelectionModel} from '@angular/cdk/collections';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

import {MatCheckbox} from '@angular/material/checkbox';
import {MatInput} from '@angular/material/input';
import {MatFormField, MatLabel} from '@angular/material/form-field';

@Component({
    selector: 'app-line-selection-dialog',
    templateUrl: './line-selection-dialog.component.html',
    styleUrls: ['./line-selection-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatLabel, MatInput, MatPaginator, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCheckbox, MatCellDef, MatCell, MatSortHeader, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatProgressSpinner, MatButton]
})
export class LineSelectionDialogComponent implements OnInit {
  private readonly dialogRef = inject<MatDialogRef<LineSelectionDialogComponent>>(MatDialogRef);
  private readonly entityService = inject(EntityService);
  private readonly data = inject(MAT_DIALOG_DATA);


  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  private entityKey: EntityKey;
  private currentSelection: string;
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly entity = signal<Entity>(undefined!);
  readonly lines: MatTableDataSource<EntityLineHeader> = new MatTableDataSource<EntityLineHeader>();
  selection: SelectionModel<EntityLineHeader> = new SelectionModel<EntityLineHeader>(true, [], false);
  displayedColumns: string[] = ['select', 'lineId', 'description', 'tag'];
  readonly selectedQuantity = signal<number>(undefined!);

  constructor() {
    const data = this.data;

    this.entityKey = data.entityKey;
    this.currentSelection = data.selectedLines;
  }

  ngOnInit(): void {
    this.isBusy.set(true);
    this.entityService.getEntity(this.entityKey.entityName, this.entityKey.entityId, this.entityKey.entityRevision).subscribe({
      next: (entity) => {
        this.entity.set(entity);
        this.lines.data = entity.lines;
        this.lines.sort = this.sort;
        this.lines.paginator = this.paginator;
        this.markCurrentSelections();
      },
      error: (error) => this.handleError(error),
      complete: () => this.isBusy.set(false)
    });
  }

  private markCurrentSelections(): void {
    if ((this.lines.data?.length || 0) > 0 && (this.currentSelection?.length || 0) > 0) {
      const selectedLines = this.currentSelection.split(',');
      const linesToSelect = this.lines.data.filter(line => selectedLines.indexOf(line.lineId) >= 0);
      this.selection.select(...linesToSelect);
      this.updateSelectedQuantity();
    }
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  masterToggle(): void {
    this.isAllSelected() ?
      this.lines.filteredData.forEach(row => this.selection.deselect(row)) :
      this.lines.filteredData.forEach(row => this.selection.select(row));
    this.updateSelectedQuantity();
  }

  private updateSelectedQuantity() {
    this.selectedQuantity.set(this.selection.selected?.length || 0);
  }

  isAllSelected(): boolean {
    return (this.selectedQuantity() || 0) > 0 && this.selectedQuantity() === (this.lines.filteredData?.length || 0);
  }

  toggleRowSelection(row: EntityLineHeader): void {
    this.selection.toggle(row);
    this.updateSelectedQuantity();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  isSelectionPresent(): boolean {
    return (this.selectedQuantity() || 0) > 0;
  }

  save(): void {
    if (this.isSelectionPresent()) {
      this.dialogRef.close(this.selection.selected);
    }
  }

}
